/**
 * Marketing product section for partner pages.
 * Shows a 5-feature grid (Website Builder, Social Media, GBP Optimizer,
 * Projects & Geotagging, Review Manager) with brand illustration icons
 * and a dashboard screenshot.
 * Used when productType is "Marketing".
 *
 * Does not handle data fetching or routing logic.
 * Depends on: ProductSectionLayout, next/image.
 */
import Image from "next/image";

import ProductSectionLayout from "@/components/partners/ProductSectionLayout";

const MARKETING_FEATURES: { title: string; description: string; icon: string }[] = [
  {
    title: "Website Builder",
    description: "AI builds your website. You just answer a few questions.",
    icon: "/marketing-icons/website-builder.svg",
  },
  {
    title: "Social Media Manager",
    description: "Schedule and post across platforms. No jumping between apps.",
    icon: "/marketing-icons/social-media.svg",
  },
  {
    title: "Google Business Profile Optimizer",
    description: "Rank higher on Google. Get found by clients near you.",
    icon: "/marketing-icons/gbp-optimizer.svg",
  },
  {
    title: "Projects & Geotagging",
    description: "Pin your work on a map. Let results sell for you.",
    icon: "/marketing-icons/geotagging.svg",
  },
  {
    title: "Review Manager",
    description: "Every review, one dashboard. Respond fast and build trust.",
    icon: "/marketing-icons/review-manager.svg",
  },
];

export default function MarketingProductSection() {
  return (
    <ProductSectionLayout
      badge={<span className="text-rebolt-500 text-xs font-bold uppercase tracking-[1px]">Overview</span>}
      heading={
        <>
          Track traffic, leads, and
          <br className="hidden md:block" />
          {" customer activity at a glance."}
        </>
      }
      description={
        <>
          Monitor visitors, pageviews, form submissions, and phone calls
          <br className="hidden md:block" />
          {" while easily managing your site pages and content."}
        </>
      }
      footerText="Manage key website pages — all from one place."
    >
      {/* Feature grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 w-full py-8 md:py-12">
        {MARKETING_FEATURES.map((feature) => (
          <div key={feature.title} className="flex flex-col items-center gap-4 p-5 text-center">
            <div className="w-20 h-20 flex items-center justify-center p-2">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={feature.icon} alt={feature.title} className="w-full h-full object-contain" />
            </div>
            <div className="flex flex-col gap-2">
              <h3 className="text-ash-200 text-lg font-semibold leading-snug">{feature.title}</h3>
              <p className="text-ash-400 text-sm leading-relaxed tracking-tight">{feature.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Dashboard screenshot */}
      <div className="w-full max-w-[1140px] overflow-hidden rounded-t-2xl shadow-[0px_23px_25px_-27px_rgba(0,0,0,0.2),0px_9px_50px_-7px_rgba(0,0,0,0.15)]">
        <div className="relative w-full aspect-[2/1]">
          <Image src="/marketing-dashboard.png" alt="Rebolt Marketing Dashboard" fill className="object-cover object-top" />
        </div>
      </div>
    </ProductSectionLayout>
  );
}
